import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  FilterBar,
  PageHeader,
  SearchField,
  Select,
  Skeleton,
  Table,
  Textarea,
  badgeToneForStatus,
} from "@evolua/ui";
import { useMemo, useState } from "react";
import {
  useCreateHrAnnouncement,
  useHrAnnouncementReach,
  useHrAnnouncements,
  usePublishHrAnnouncement,
} from "../api/use-hr.js";

const RH_DEPARTMENT_ID = "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb";

/** Comunicação interna do RH (FASE-06-T52). */
export function ComunicadosRhPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | "DRAFT" | "SCHEDULED" | "PUBLISHED">("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(status ? { status } : {}),
    }),
    [q, status],
  );

  const query = useHrAnnouncements(filters);
  const create = useCreateHrAnnouncement();
  const publish = usePublishHrAnnouncement();
  const reach = useHrAnnouncementReach(selectedId);

  const correlationId =
    query.error instanceof EvoluaApiError ? query.error.problem.traceId : undefined;

  async function onCreate(publishNow: boolean) {
    setActionError(null);
    if (!title.trim() || !body.trim()) {
      setActionError("Informe título e texto.");
      return;
    }
    try {
      const item = await create.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        departmentId: RH_DEPARTMENT_ID,
        ...(schedule && !publishNow
          ? { scheduledAt: new Date(Date.now() + 86_400_000).toISOString() }
          : {}),
        ...(publishNow ? { publishNow: true } : {}),
      });
      setTitle("");
      setBody("");
      setSchedule(false);
      setSelectedId(item.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha ao criar");
    }
  }

  async function onPublish(id: string) {
    setActionError(null);
    try {
      await publish.mutateAsync(id);
      setSelectedId(id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha ao publicar");
    }
  }

  if (query.isPending) {
    return <Skeleton height="8rem" label="Comunicados RH" />;
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Comunicados (RH)"
        lead="Segmentação por departamento, agendamento e relatório de leitura/ciência."
      />

      <section className="evolua-stack evolua-stack--3">
        <Field
          label="Título"
          placeholder="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Título do comunicado"
        />
        <Textarea
          label="Texto do comunicado"
          placeholder="Texto do comunicado"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          aria-label="Texto do comunicado"
          rows={4}
        />
        <Checkbox
          label="Agendar para amanhã (em vez de rascunho)"
          checked={schedule}
          onChange={(e) => setSchedule(e.target.checked)}
        />
        <div className="evolua-stack evolua-stack--3">
          <Button
            type="button"
            size="sm"
            disabled={create.isPending}
            onClick={() => void onCreate(false)}
          >
            {create.isPending ? "Salvando…" : schedule ? "Agendar → RH" : "Rascunho → RH"}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={create.isPending}
            onClick={() => void onCreate(true)}
          >
            Publicar agora → RH
          </Button>
        </div>
      </section>

      <FilterBar>
        <SearchField
          label="Buscar"
          placeholder="Buscar…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar comunicados"
        />
        <Select
          label="Status"
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as "" | "DRAFT" | "SCHEDULED" | "PUBLISHED")
          }
          aria-label="Status"
          options={[
            { value: "", label: "Todos os status" },
            { value: "DRAFT", label: "Rascunho" },
            { value: "SCHEDULED", label: "Agendado" },
            { value: "PUBLISHED", label: "Publicado" },
          ]}
        />
      </FilterBar>

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      {query.data.data.length === 0 ? (
        <EmptyState title="Nenhum comunicado" description="Crie o primeiro acima." />
      ) : (
        <Table
          caption="Comunicados"
          getRowId={(item) => item.id}
          columns={[
            { id: "title", header: "Título", cell: (item) => item.title },
            {
              id: "status",
              header: "Status",
              cell: (item) => (
                <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
              ),
            },
            {
              id: "reach",
              header: "Alcance",
              cell: (item) =>
                `${item.ackCount}/${item.audienceCount} ciência · ${item.readCount} lidos`,
            },
            {
              id: "body",
              header: "Texto",
              cell: (item) =>
                item.body.length > 72 ? `${item.body.slice(0, 72)}…` : item.body,
            },
            {
              id: "actions",
              header: "Ações",
              cell: (item) => (
                <>
                  {item.status !== "PUBLISHED" ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={publish.isPending}
                      onClick={() => void onPublish(item.id)}
                    >
                      Publicar
                    </Button>
                  ) : null}{" "}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setSelectedId(item.id)}
                  >
                    Relatório
                  </Button>
                </>
              ),
            },
          ]}
          rows={query.data.data}
        />
      )}

      {selectedId && reach.data ? (
        <section className="evolua-stack evolua-stack--3">
          <h2 className="evolua-section-title">Alcance</h2>
          <p className="evolua-page-lead">
            {reach.data.acked}/{reach.data.targeted} com ciência · {reach.data.read} lidos
          </p>
          <Table
            caption="Alcance"
            getRowId={(row) => row.employeeId}
            columns={[
              {
                id: "employee",
                header: "Colaborador",
                cell: (row) => `${row.employeeId.slice(0, 8)}…`,
              },
              {
                id: "status",
                header: "Status",
                cell: (row) =>
                  row.ackedAt ? "ciência" : row.readAt ? "lido" : "pendente",
              },
            ]}
            rows={reach.data.items}
          />
        </section>
      ) : null}
    </>
  );
}
